import { LearnResourcesSection } from '@/components/learning/learn-resources-section'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import {
  categoryColors,
  categoryLabels,
  difficultyColors,
  difficultyLabels,
} from '@/constants/learning'
import { useCurrentResume } from '@/hooks/use-documents'
import { useIsMobile } from '@/hooks/use-is-mobile'
import {
  useArchiveJobTopics,
  useArchiveLearnTopic,
  useFailedTopicAutoRecovery,
  useGenerateTopic,
  useInitializeLearning,
  useTopics,
} from '@/hooks/use-learning'
import { cn } from '@/lib/utils'
import type { LearningTopicWithProgress } from '@/types'
import {
  AlertCircle,
  Building2,
  ChevronDown,
  ChevronRight,
  GraduationCap,
  Library,
  Loader2,
  MapPin,
  Play,
  RefreshCw,
  Sparkles,
  Trash2,
} from 'lucide-react'
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

// --- Utility ---

function TopicBadges({ topic }: { topic: LearningTopicWithProgress }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-1">
      <span
        className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${categoryColors[topic.skill_category]}`}
      >
        {categoryLabels[topic.skill_category]}
      </span>
      <span
        className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${difficultyColors[topic.difficulty]}`}
      >
        {difficultyLabels[topic.difficulty]}
      </span>
    </div>
  )
}

function ProgressBar({ percent, className }: { percent: number; className?: string }) {
  return (
    <div className={`w-full bg-muted rounded-full h-1.5 ${className ?? ''}`}>
      <div
        className="bg-blue-600 h-1.5 rounded-full transition-all"
        style={{ width: `${Math.min(percent, 100)}%` }}
      />
    </div>
  )
}

// --- Global Continue Banner ---

function ContinueBanner({ topic }: { topic: LearningTopicWithProgress }) {
  const navigate = useNavigate()

  return (
    <button
      type="button"
      onClick={() => navigate(`/learn/${topic.id}`)}
      className="w-full mb-6 flex items-center gap-4 p-4 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors text-left group"
    >
      <div className="p-2.5 rounded-full bg-blue-100 group-hover:bg-blue-200 transition-colors shrink-0">
        <Play className="h-4 w-4 text-blue-600 fill-blue-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-blue-600 mb-0.5">Continue where you left off</p>
        <p className="font-medium truncate">{topic.title}</p>
        <div className="flex items-center gap-3 mt-1.5">
          <ProgressBar percent={topic.progress_percent} className="max-w-32" />
          <span className="text-xs text-muted-foreground shrink-0">
            {topic.completed_modules}/{topic.modules_count} modules
          </span>
        </div>
      </div>
      <ChevronRight className="h-5 w-5 text-blue-400 group-hover:text-blue-600 transition-colors shrink-0" />
    </button>
  )
}

// --- In-Progress Topic Card ---

function InProgressCard({
  topic,
  onClick,
  showArchive,
  onArchive,
  isArchiving,
}: {
  topic: LearningTopicWithProgress
  onClick: () => void
  showArchive?: boolean
  onArchive?: () => void
  isArchiving?: boolean
}) {
  return (
    <Card className="group mb-3 transition-shadow hover:shadow-md">
      <CardContent className="flex items-center gap-2 py-4">
        <button
          type="button"
          onClick={onClick}
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-4 text-left"
        >
          <div className="shrink-0 rounded-lg bg-blue-50 p-2.5 transition-colors group-hover:bg-blue-100">
            <Play className="h-4 w-4 text-blue-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium">{topic.title}</p>
            <TopicBadges topic={topic} />
            <div className="mt-1.5 flex items-center gap-3">
              <ProgressBar percent={topic.progress_percent} className="max-w-40" />
              <span className="shrink-0 text-xs text-muted-foreground">
                {topic.completed_modules} / {topic.modules_count} modules · {topic.progress_percent}
                %
              </span>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground transition-colors group-hover:text-blue-600" />
        </button>
        {showArchive && onArchive && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onArchive()
            }}
            disabled={isArchiving}
            aria-label="Archive topic"
            title="Archive topic"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-transparent text-muted-foreground transition-colors hover:bg-muted hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          >
            {isArchiving ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Trash2 className="h-4 w-4" aria-hidden />
            )}
          </button>
        )}
      </CardContent>
    </Card>
  )
}

// --- Up Next Row ---

function UpNextRow({
  topic,
  onClick,
  showArchive,
  onArchive,
  isArchiving,
}: {
  topic: LearningTopicWithProgress
  onClick: () => void
  showArchive?: boolean
  onArchive?: () => void
  isArchiving?: boolean
}) {
  const isGenerating =
    topic.generation_status === 'pending' || topic.generation_status === 'generating'
  const isFailed = topic.generation_status === 'failed'

  return (
    <div className="flex items-stretch gap-1 px-2">
      {isGenerating ? (
        <div
          className="flex min-w-0 flex-1 cursor-not-allowed items-center gap-2 rounded-lg px-2 py-3 text-left text-muted-foreground"
          aria-busy="true"
          aria-label="Learning topic still being prepared"
        >
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-blue-500" aria-hidden />
          <span className="text-sm">Curating topics to address skill gaps</span>
        </div>
      ) : isFailed ? (
        <div className="flex min-w-0 flex-1 items-start gap-3 rounded-lg px-2 py-2.5 text-left opacity-70">
          <AlertCircle className="h-4 w-4 shrink-0 self-center text-destructive" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-muted-foreground line-through">
              {topic.title}
            </p>
            <p className="mt-0.5 text-xs text-destructive">
              {topic.generation_error || 'Failed to generate content'}
            </p>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={onClick}
          className="group flex min-w-0 flex-1 items-start gap-3 rounded-lg px-2 py-2.5 text-left transition-colors hover:bg-accent"
        >
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{topic.title}</p>
            <TopicBadges topic={topic} />
            <p className="mt-1 text-xs text-muted-foreground">
              {topic.completed_modules} / {topic.modules_count} modules
            </p>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 self-center text-muted-foreground transition-colors group-hover:text-blue-600" />
        </button>
      )}
      {showArchive && onArchive && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onArchive()
          }}
          disabled={isArchiving || isGenerating}
          aria-label="Archive topic"
          title="Archive topic"
          className="flex h-9 w-9 shrink-0 items-center justify-center self-center rounded-md border border-transparent text-muted-foreground transition-colors hover:bg-muted hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
        >
          {isArchiving ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Trash2 className="h-4 w-4" aria-hidden />
          )}
        </button>
      )}
    </div>
  )
}

// --- Completed Chips ---

function CompletedChips({
  topics,
  onTopicClick,
  showArchive,
  onArchiveTopic,
  archivingTopicId,
}: {
  topics: LearningTopicWithProgress[]
  onTopicClick: (id: number) => void
  showArchive?: boolean
  onArchiveTopic?: (id: number) => void
  archivingTopicId?: number | null
}) {
  const [expanded, setExpanded] = useState(false)

  if (topics.length === 0) return null

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mb-2"
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 transition-transform" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 transition-transform" />
        )}
        Completed ({topics.length})
      </button>
      {expanded && (
        <ul className="mt-2 flex flex-col gap-2">
          {topics.map((topic) => (
            <li key={topic.id} className="flex items-stretch gap-1">
              <button
                type="button"
                onClick={() => onTopicClick(topic.id)}
                className="flex min-w-0 flex-1 items-center justify-between gap-3 rounded-lg border bg-muted/30 px-3 py-2 text-left transition-colors hover:bg-accent"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{topic.title}</p>
                  <TopicBadges topic={topic} />
                  <p className="mt-1 text-xs text-muted-foreground">
                    {topic.completed_modules} / {topic.modules_count} modules
                  </p>
                </div>
                <span className="shrink-0 rounded-md border border-green-300 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-600 dark:border-green-700 dark:bg-green-950/30">
                  Done
                </span>
              </button>
              {showArchive && onArchiveTopic && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onArchiveTopic(topic.id)
                  }}
                  disabled={archivingTopicId === topic.id}
                  aria-label="Archive topic"
                  title="Archive topic"
                  className="flex h-9 w-9 shrink-0 items-center justify-center self-center rounded-md border border-transparent text-muted-foreground transition-colors hover:bg-muted hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                >
                  {archivingTopicId === topic.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <Trash2 className="h-4 w-4" aria-hidden />
                  )}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// --- Job Section ---

interface JobGroup {
  job_posting_id: number | null
  job_context: string
  job_company_name?: string | null
  job_listing_title?: string | null
  job_location?: string | null
  topics: LearningTopicWithProgress[]
}

/** Split legacy `job_context` strings like "Title at Company" when structured fields are absent. */
function splitJobContextString(jobContext: string | undefined | null): {
  title: string
  company: string | null
} {
  if (!jobContext?.trim()) return { title: 'General', company: null }
  const jc = jobContext.trim()
  const sep = ' at '
  const idx = jc.lastIndexOf(sep)
  if (idx <= 0) return { title: jc, company: null }
  return {
    title: jc.slice(0, idx).trim(),
    company: jc.slice(idx + sep.length).trim() || null,
  }
}

function jobSectionHeaderDisplay(group: JobGroup) {
  const split = splitJobContextString(group.job_context)
  const company =
    group.job_company_name?.trim() || group.topics[0]?.job_company_name?.trim() || split.company
  const jobTitle =
    group.job_listing_title?.trim() || group.topics[0]?.job_listing_title?.trim() || split.title
  const location = group.job_location?.trim() || group.topics[0]?.job_location?.trim() || null
  return {
    company: company || null,
    jobTitle,
    location,
  }
}

function jobGroupSectionKey(group: JobGroup): string {
  return group.job_posting_id != null ? String(group.job_posting_id) : 'general'
}

function defaultExpandedForJobGroup(group: JobGroup): boolean {
  return group.topics.some(
    (t) =>
      t.progress_percent < 100 ||
      t.generation_status === 'pending' ||
      t.generation_status === 'generating' ||
      t.generation_status === 'failed',
  )
}

function JobSection({
  group,
  onTopicClick,
  onRemove,
  isRemoving,
  sectionKey,
  expanded,
  onToggleExpand,
  allowTopicArchive = false,
  onArchiveTopic,
  archivingTopicId = null,
}: {
  group: JobGroup
  onTopicClick: (id: number) => void
  onRemove: (() => void) | null
  isRemoving: boolean
  sectionKey: string
  expanded: boolean
  onToggleExpand: () => void
  allowTopicArchive?: boolean
  onArchiveTopic?: (id: number) => void
  archivingTopicId?: number | null
}) {
  const inProgress = group.topics.filter(
    (t) =>
      t.progress_percent > 0 && t.progress_percent < 100 && t.generation_status === 'completed',
  )
  const upNextBase = group.topics
    .filter(
      (t) =>
        t.progress_percent === 0 ||
        t.generation_status === 'pending' ||
        t.generation_status === 'generating' ||
        t.generation_status === 'failed',
    )
    .filter((t) => t.progress_percent < 100)
  const topicIsGenerating = (t: LearningTopicWithProgress) =>
    t.generation_status === 'pending' || t.generation_status === 'generating'
  const upNextReady = upNextBase.filter((t) => !topicIsGenerating(t))
  const upNextGenerating = upNextBase.filter((t) => topicIsGenerating(t))
  const upNext = [...upNextReady, ...upNextGenerating]
  const completed = group.topics.filter((t) => t.progress_percent === 100)

  const isGeneratingGroup = group.topics.some(topicIsGenerating)
  const hasAnyReadyTopic = inProgress.length > 0 || upNextReady.length > 0 || completed.length > 0
  const showUpNextLabel = inProgress.length > 0 || completed.length > 0

  const totalTopics = group.topics.length
  const completedTopics = completed.length
  const sectionPercent = totalTopics > 0 ? Math.round((completedTopics / totalTopics) * 100) : 0
  const panelId = `job-skills-${sectionKey}`
  const { company, jobTitle, location } = jobSectionHeaderDisplay(group)

  return (
    <Card className="mb-6 overflow-hidden">
      <CardHeader className="space-y-0 p-4 pb-0">
        <div className="flex items-start gap-2 border-b border-border pb-4">
          <button
            type="button"
            onClick={onToggleExpand}
            aria-expanded={expanded}
            aria-controls={panelId}
            className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-transparent text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronDown
              className={cn('h-4 w-4 transition-transform', expanded ? 'rotate-0' : '-rotate-90')}
              aria-hidden
            />
          </button>
          <div className="min-w-0 flex-1">
            {company && (
              <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
                <Building2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span className="truncate">{company}</span>
              </div>
            )}
            <h2 className="text-base font-semibold leading-snug text-foreground">{jobTitle}</h2>
            {location && (
              <div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground">
                <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span className="truncate">{location}</span>
              </div>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              <ProgressBar percent={sectionPercent} className="w-24 sm:w-40 shrink-0" />
              <span className="shrink-0">
                {completedTopics}/{totalTopics} skills complete
              </span>
            </div>
          </div>
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              disabled={isRemoving}
              aria-label="Delete learning path"
              title="Delete learning path"
              className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-transparent text-muted-foreground hover:bg-muted hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            >
              {isRemoving ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Trash2 className="h-4 w-4" aria-hidden />
              )}
            </button>
          )}
        </div>
      </CardHeader>

      {expanded && (
        <CardContent id={panelId} className="space-y-4 p-4 pt-4">
          {isGeneratingGroup && !hasAnyReadyTopic && (
            <div
              className="flex min-h-[160px] items-center justify-center rounded-lg border bg-muted/40 px-4 py-8 text-center"
              aria-busy="true"
              aria-live="polite"
            >
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" aria-hidden />
                <p className="text-sm text-muted-foreground">
                  Curating topics to address skill gaps
                </p>
              </div>
            </div>
          )}
          {isGeneratingGroup && hasAnyReadyTopic && (
            <div
              className="flex items-center gap-3 rounded-lg border border-blue-200/80 bg-blue-50/80 px-4 py-3"
              aria-busy="true"
              aria-live="polite"
            >
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-blue-600" aria-hidden />
              <p className="text-sm text-muted-foreground">
                Curating topics to address skill gaps — more skills will appear here as they&apos;re
                ready.
              </p>
            </div>
          )}

          {inProgress.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-medium text-muted-foreground">In Progress</p>
              {inProgress.map((topic) => (
                <InProgressCard
                  key={topic.id}
                  topic={topic}
                  onClick={() => onTopicClick(topic.id)}
                  isArchiving={archivingTopicId === topic.id}
                  {...(allowTopicArchive && onArchiveTopic
                    ? {
                        showArchive: true,
                        onArchive: () => onArchiveTopic(topic.id),
                      }
                    : {})}
                />
              ))}
            </div>
          )}

          {upNext.length > 0 && (
            <div>
              {showUpNextLabel && (
                <p className="mb-1 text-xs font-medium text-muted-foreground">Up Next</p>
              )}
              <div className="divide-y rounded-lg border">
                {upNext.map((topic) => (
                  <UpNextRow
                    key={topic.id}
                    topic={topic}
                    onClick={() => onTopicClick(topic.id)}
                    isArchiving={archivingTopicId === topic.id}
                    {...(allowTopicArchive && onArchiveTopic
                      ? {
                          showArchive: true,
                          onArchive: () => onArchiveTopic(topic.id),
                        }
                      : {})}
                  />
                ))}
              </div>
            </div>
          )}

          <CompletedChips
            topics={completed}
            onTopicClick={onTopicClick}
            {...(allowTopicArchive && onArchiveTopic
              ? {
                  showArchive: true,
                  onArchiveTopic,
                  archivingTopicId,
                }
              : {})}
          />
        </CardContent>
      )}
    </Card>
  )
}

// --- Empty State ---

function EmptyState({ noResume }: { noResume: boolean }) {
  const isMobile = useIsMobile()
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <Sparkles className="mx-auto mb-4 h-12 w-12 text-blue-400" />
        <h3 className="mb-2 text-lg font-semibold">No learning path yet</h3>
        {noResume ? (
          <>
            <p className="mx-auto mb-6 max-w-sm text-muted-foreground">
              Upload your resume and match with jobs to get a personalised skill plan tailored to
              that specific role.
            </p>
            <Link to="/resume">
              <Button size="lg" className="gap-2 bg-blue-600 text-white hover:bg-blue-700">
                <ChevronRight className="h-4 w-4" />
                Go to Resume
              </Button>
            </Link>
          </>
        ) : (
          <>
            <p className="mx-auto mb-6 max-w-sm text-muted-foreground">
              Analyse a job match to get a personalised skill plan tailored to that specific role.
            </p>
            <Link to="/opportunities">
              <Button size="lg" className="gap-2 bg-blue-600 text-white hover:bg-blue-700">
                <ChevronRight className="h-4 w-4" />
                {isMobile ? 'Go to Jobs' : 'Go to Opportunities'}
              </Button>
            </Link>
          </>
        )}
      </CardContent>
    </Card>
  )
}

// --- Job Search Essentials ---

function EssentialsSection({ onTopicClick }: { onTopicClick: (id: number) => void }) {
  const { data, isLoading } = useTopics(1, 50, true)
  const essentials = useMemo(() => (data?.items ?? []).filter((t) => t.is_popular), [data])

  if (isLoading || essentials.length === 0) return null

  return (
    <div className="mt-8 pt-6 border-t">
      <h2 className="text-sm font-semibold text-foreground mb-1">Job Search Essentials</h2>
      <p className="text-xs text-muted-foreground mb-4">
        Skills every job seeker benefits from, regardless of role.
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        {essentials.map((topic) => (
          <button
            key={topic.id}
            type="button"
            onClick={() => onTopicClick(topic.id)}
            className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-accent text-left transition-colors group"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{topic.title}</p>
              <TopicBadges topic={topic} />
              <p className="text-xs text-muted-foreground mt-1">
                {topic.completed_modules} / {topic.modules_count} modules
                {topic.progress_percent > 0 ? ` · ${topic.progress_percent}%` : ''}
              </p>
              {topic.progress_percent > 0 && (
                <div className="flex items-center gap-2 mt-1">
                  <ProgressBar percent={topic.progress_percent} className="max-w-24" />
                </div>
              )}
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-blue-600 transition-colors shrink-0" />
          </button>
        ))}
      </div>
    </div>
  )
}

// --- Main Page ---

export function LearnPage() {
  const location = useLocation()
  const { data, isLoading, isError } = useTopics(1, 50, false)
  const { currentResume, isLoading: documentsLoading } = useCurrentResume()
  const emptyStateNoResume = !documentsLoading && currentResume == null
  const generate = useGenerateTopic()
  const initializeLearning = useInitializeLearning()
  const archiveJobTopics = useArchiveJobTopics()
  const archiveLearnTopic = useArchiveLearnTopic()
  const navigate = useNavigate()
  const [removingJobId, setRemovingJobId] = useState<number | null>(null)
  const [archivingTopicId, setArchivingTopicId] = useState<number | null>(null)
  const [curiousOpen, setCuriousOpen] = useState(false)
  const [jobSectionExpanded, setJobSectionExpanded] = useState<Record<string, boolean>>({})
  const spotlightGenerateRetried = useRef(false)

  const isJobSectionExpanded = (group: JobGroup) => {
    const key = jobGroupSectionKey(group)
    const explicit = jobSectionExpanded[key]
    if (explicit !== undefined) return explicit
    return defaultExpandedForJobGroup(group)
  }

  const toggleJobSection = (group: JobGroup) => {
    const key = jobGroupSectionKey(group)
    setJobSectionExpanded((prev) => ({
      ...prev,
      [key]: !(prev[key] ?? defaultExpandedForJobGroup(group)),
    }))
  }

  useEffect(() => {
    if (location.hash !== '#resource-library') return
    const id = window.requestAnimationFrame(() => {
      document
        .getElementById('resource-library')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
    return () => window.cancelAnimationFrame(id)
  }, [location.hash])

  // Keyboard shortcut: / to open spotlight
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (
        e.key === '/' &&
        !curiousOpen &&
        !(e.target instanceof HTMLInputElement) &&
        !(e.target instanceof HTMLTextAreaElement) &&
        !(e.target instanceof HTMLSelectElement)
      ) {
        e.preventDefault()
        setCuriousOpen(true)
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [curiousOpen])

  const topics = data?.items ?? []

  useFailedTopicAutoRecovery(topics)

  // Global "Continue" — most recently updated in-progress topic
  const continueTopic = useMemo(
    () =>
      [...topics]
        .filter(
          (t) =>
            t.progress_percent > 0 &&
            t.progress_percent < 100 &&
            t.generation_status === 'completed',
        )
        .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())[0] ??
      null,
    [topics],
  )

  // Group by job_posting_id, sort groups by MAX(updated_at) desc
  const jobGroups = useMemo((): JobGroup[] => {
    const map = new Map<number | null, LearningTopicWithProgress[]>()
    for (const topic of topics) {
      const key = topic.job_posting_id ?? null
      map.set(key, [...(map.get(key) ?? []), topic])
    }
    return [...map.entries()]
      .map(([job_posting_id, groupTopics]) => {
        const first = groupTopics[0]
        return {
          job_posting_id,
          job_context: first?.job_context ?? 'General',
          job_company_name: first?.job_company_name ?? null,
          job_listing_title: first?.job_listing_title ?? null,
          job_location: first?.job_location ?? null,
          topics: groupTopics,
        }
      })
      .sort((a, b) => {
        const maxUpdated = (ts: LearningTopicWithProgress[]) =>
          Math.max(...ts.map((t) => new Date(t.updated_at).getTime()))
        return maxUpdated(b.topics) - maxUpdated(a.topics)
      })
  }, [topics])

  const handleRemoveJobPath = (group: JobGroup) => {
    if (group.job_posting_id === null) return
    setRemovingJobId(group.job_posting_id)
    archiveJobTopics.mutate(group.job_posting_id, {
      onSettled: () => setRemovingJobId(null),
    })
  }

  const handleArchiveGeneralTopic = (topicId: number) => {
    setArchivingTopicId(topicId)
    archiveLearnTopic.mutate(topicId, {
      onSettled: () => setArchivingTopicId(null),
    })
  }

  const scrollToResourceLibrary = () => {
    if (location.pathname === '/learn') {
      document
        .getElementById('resource-library')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } else {
      navigate('/learn#resource-library')
    }
  }

  return (
    <div className="h-full overflow-y-auto bg-[#F3F4F6] dark:bg-background">
      <div className="p-4 md:p-6">
        {/* Page Header */}
        <div className="mb-6 flex items-center justify-between gap-2 flex-wrap">
          <h1 className="flex items-center gap-2.5 text-2xl font-semibold">
            <GraduationCap className="h-6 w-6 shrink-0 text-foreground" aria-hidden />
            Learn
          </h1>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={scrollToResourceLibrary}
              className="flex items-center gap-2 rounded-full shrink-0"
            >
              <Library className="h-4 w-4 text-blue-600" aria-hidden />
              <span>Resource library</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCuriousOpen(true)}
              className="flex items-center gap-2 group rounded-full shrink-0"
            >
              <Sparkles className="h-4 w-4 text-blue-600" />
              <span>Explore topics</span>
              <kbd className="hidden sm:inline-flex h-5 items-center rounded border bg-muted px-1.5 font-mono text-[10px] text-muted-foreground group-hover:border-blue-300">
                /
              </kbd>
            </Button>
          </div>
        </div>

        {/* Global Continue Banner */}
        {continueTopic && <ContinueBanner topic={continueTopic} />}

        {!isLoading && !isError && jobGroups.length > 0 && (
          <p className="mb-6 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Build the skills that strengthen your profile for the roles you&apos;re pursuing. Each
            path lines up with where you want to land.
          </p>
        )}

        {/* Topics */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : isError ? (
          <Card>
            <CardContent className="py-12 text-center">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
              <p className="text-muted-foreground mb-4">
                Failed to load your learning topics. Please try again.
              </p>
              <Button variant="outline" size="sm" onClick={() => initializeLearning.mutate()}>
                <RefreshCw className="h-4 w-4 mr-1" />
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : jobGroups.length === 0 ? (
          <EmptyState noResume={emptyStateNoResume} />
        ) : (
          jobGroups.map((group) => (
            <JobSection
              key={group.job_posting_id ?? 'general'}
              sectionKey={jobGroupSectionKey(group)}
              expanded={isJobSectionExpanded(group)}
              onToggleExpand={() => toggleJobSection(group)}
              group={group}
              onTopicClick={(id) => navigate(`/learn/${id}`)}
              onRemove={group.job_posting_id !== null ? () => handleRemoveJobPath(group) : null}
              isRemoving={removingJobId === group.job_posting_id}
              allowTopicArchive={group.job_posting_id === null}
              onArchiveTopic={handleArchiveGeneralTopic}
              archivingTopicId={archivingTopicId}
            />
          ))
        )}

        {/* Job Search Essentials — always visible */}
        {!isLoading && <EssentialsSection onTopicClick={(id) => navigate(`/learn/${id}`)} />}

        <LearnResourcesSection />

        {/* Spotlight overlay */}
        {curiousOpen && (
          <SpotlightOverlay
            onClose={() => setCuriousOpen(false)}
            onGenerate={(skill) => {
              setCuriousOpen(false)
              spotlightGenerateRetried.current = false
              generate.mutate(
                { skillName: skill },
                {
                  onSuccess: (topic) => {
                    spotlightGenerateRetried.current = false
                    if (topic?.id) navigate(`/learn/${topic.id}`)
                  },
                  onError: () => {
                    if (!spotlightGenerateRetried.current) {
                      spotlightGenerateRetried.current = true
                      window.setTimeout(() => {
                        generate.mutate(
                          { skillName: skill },
                          {
                            onSuccess: (topic) => {
                              spotlightGenerateRetried.current = false
                              if (topic?.id) navigate(`/learn/${topic.id}`)
                            },
                            onError: () => {
                              spotlightGenerateRetried.current = false
                              generate.reset()
                            },
                          },
                        )
                      }, 400)
                    } else {
                      spotlightGenerateRetried.current = false
                      generate.reset()
                    }
                  },
                },
              )
            }}
            isGenerating={generate.isPending}
          />
        )}
      </div>
    </div>
  )
}

// --- Spotlight Overlay (minimal, self-contained) ---

function SpotlightOverlay({
  onClose,
  onGenerate,
  isGenerating,
}: {
  onClose: () => void
  onGenerate: (skill: string) => void
  isGenerating: boolean
}) {
  const [skill, setSkill] = useState('')

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [onClose])

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (skill.trim()) onGenerate(skill.trim())
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]">
      <div
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
        }}
        role="button"
        tabIndex={-1}
      />
      <div className="relative w-full max-w-lg mx-4">
        <div className="rounded-xl border bg-card shadow-2xl overflow-hidden">
          <form onSubmit={handleSubmit}>
            <div className="flex items-center border-b px-4">
              <Sparkles className="h-5 w-5 text-blue-600 shrink-0" />
              <input
                // biome-ignore lint: autofocus is intentional for user-triggered spotlight
                autoFocus
                type="text"
                placeholder="What would you like to learn?"
                value={skill}
                onChange={(e) => setSkill(e.target.value)}
                className="flex-1 bg-transparent py-4 px-3 text-lg outline-none placeholder:text-muted-foreground/60"
              />
              {skill.trim() && (
                <Button
                  type="submit"
                  size="sm"
                  disabled={isGenerating}
                  className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {isGenerating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Sparkles className="h-3.5 w-3.5 mr-1" />
                      Generate
                    </>
                  )}
                </Button>
              )}
            </div>
          </form>
          <div className="border-t px-4 py-2 flex items-center justify-between text-xs text-muted-foreground">
            <span>Type any skill or topic</span>
            <kbd className="px-1.5 py-0.5 rounded border bg-muted font-mono text-[10px]">esc</kbd>
          </div>
        </div>
      </div>
    </div>
  )
}
